import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'auditor' | 'user';
  isActive: boolean;
  emailVerified: boolean;
  mfaEnabled: boolean;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  
  // Actions
  login: (email: string, password: string, mfaToken?: string, mfaMethod?: 'totp' | 'duo') => Promise<{ requiresMFA?: boolean; requiresMFASetup?: boolean; duoAvailable?: boolean }>;
  logout: () => Promise<void>;
  register: (data: {
    email: string;
    firstName: string;
    lastName: string;
    mfaSecret?: string;
  }) => Promise<void>;
  checkAuth: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: false,
      isAuthenticated: false,

      login: async (email: string, password: string, mfaToken?: string, mfaMethod?: 'totp' | 'duo') => {
        set({ isLoading: true });
        try {
          const response = await api.login({ email, password, mfaToken, mfaMethod });
          
          if (response.requiresMFA) {
            set({ isLoading: false });
            return { requiresMFA: true, duoAvailable: (response as any).duoAvailable, totpAvailable: (response as any).totpAvailable } as any;
          }

          if (response.requiresMFASetup) {
            set({ isLoading: false });
            return { requiresMFASetup: true };
          }

          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });

          return {};
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await api.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      register: async (data) => {
        set({ isLoading: true });
        try {
          await api.register(data);
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      checkAuth: async () => {
        const token = api.getToken();
        if (!token) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await api.checkAuth();
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          console.error('Auth check failed:', error);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          api.setToken(null);
        }
      },

      updateProfile: async (data) => {
        const currentUser = get().user;
        if (!currentUser) throw new Error('No user logged in');

        set({ isLoading: true });
        try {
          const response = await api.updateProfile(data) as { user: User };
          set({
            user: response.user,
            isLoading: false,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        set({ isLoading: true });
        try {
          await api.changePassword({ currentPassword, newPassword });
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      clearAuth: () => {
        api.setToken(null);
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
